import React, { useState, useMemo } from 'react';
import type { AgingBucket } from '../../../hooks/useOutstandingCollections';
import {
  parseBucketClients,
  fmtUSD,
  riskBadgeClass,
  bucketBarColor,
  buildAgingBucketRowViews,
  type AgingBucketRowView,
} from '../../../utils/outstandingUtils';
import { AgingBucketChart } from './AgingBucketChart';

interface AgingOverviewTabProps {
  buckets: AgingBucket[];
}

// ─── Bucket summary table ─────────────────────────────────────────────────────
const BucketTable: React.FC<{
  buckets: AgingBucketRowView[];
  selectedRowKey: string | null;
  onSelectRowKey: (rowKey: string) => void;
  embedded?: boolean;
}> = ({ buckets, selectedRowKey, onSelectRowKey, embedded = false }) => {
  const table = (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-slate-100 bg-slate-50">
            {['Aging Period', 'Outstanding', '% of Total', 'Invoices', 'Clients', 'Avg Days', 'Risk'].map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {buckets.map(bucket => {
            const isSelected = selectedRowKey === bucket.rowKey;
            const barColor = bucketBarColor(bucket);
            return (
              <tr
                key={bucket.rowKey}
                onClick={() => onSelectRowKey(isSelected ? '' : bucket.rowKey)}
                className={`border-b border-slate-50 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full flex-none" style={{ backgroundColor: barColor }} />
                    <div>
                      <p className="font-semibold text-slate-800">{bucket.chartXLabel}</p>
                      <p className="text-[10px] text-slate-500">{bucket.bucket_description}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 font-bold text-slate-900 tabular-nums">
                  {fmtUSD(bucket.total_outstanding)}
                </td>
                <td className="px-4 py-3 text-slate-700 tabular-nums">
                  {bucket.percentage_of_total.toFixed(1)}%
                </td>
                <td className="px-4 py-3 text-slate-700 tabular-nums">{bucket.invoice_count}</td>
                <td className="px-4 py-3 text-slate-700 tabular-nums">{bucket.unique_clients}</td>
                <td className="px-4 py-3 text-slate-700 tabular-nums">
                  {bucket.avg_days_overdue < 0
                    ? `${Math.abs(Math.round(bucket.avg_days_overdue))}d remaining`
                    : `${Math.round(bucket.avg_days_overdue)}d overdue`}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${riskBadgeClass(bucket.collection_risk)}`}>
                    {bucket.collection_risk}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (embedded) {
    return table;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
        <div className="w-0.5 h-4 rounded-full bg-orange-500 flex-none" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
          Aging Bucket Details
        </span>
      </div>
      {table}
    </div>
  );
};

// ─── Client list for selected bucket ─────────────────────────────────────────
const BucketClientList: React.FC<{ bucket: AgingBucket }> = ({ bucket }) => {
  const clients = useMemo(() => parseBucketClients(bucket.clients_list), [bucket.clients_list]);
  const isCurrent = bucket.avg_days_overdue < 0;

  if (clients.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-md p-6 text-center text-slate-500 text-sm">
        No client details available for this bucket.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
        <div className="w-0.5 h-4 rounded-full bg-blue-500 flex-none" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
          Clients — {bucket.bucket_display_name}
        </span>
        <span className="ml-auto text-[11px] text-slate-500">{clients.length} clients</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50">
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">#</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">Client</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">Outstanding</th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {isCurrent ? 'Days Remaining' : 'Days Overdue'}
              </th>
              <th className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">Invoices</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client, i) => (
              <tr key={client.name} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 text-slate-500 tabular-nums">{i + 1}</td>
                <td className="px-4 py-3">
                  <p className="font-semibold text-slate-800">{client.name.trim()}</p>
                </td>
                <td className="px-4 py-3 font-bold text-slate-900 tabular-nums">
                  {fmtUSD(client.outstanding)}
                </td>
                <td className="px-4 py-3">
                  {client.days_overdue < 0 ? (
                    <span className="text-blue-700 font-semibold tabular-nums">
                      {Math.abs(client.days_overdue)}d remaining
                    </span>
                  ) : client.days_overdue === 0 ? (
                    <span className="text-emerald-700 font-semibold">Due today</span>
                  ) : (
                    <span className="text-red-700 font-semibold tabular-nums">
                      {client.days_overdue}d overdue
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-700 tabular-nums">{client.invoice_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────
export const AgingOverviewTab: React.FC<AgingOverviewTabProps> = ({ buckets }) => {
  /** `undefined` = use default Current bucket; `null` = user cleared selection */
  const [selectionOverride, setSelectionOverride] = useState<string | null | undefined>(undefined);
  const [view, setView] = useState<'chart' | 'table'>('table');

  const bucketViews = useMemo(() => buildAgingBucketRowViews(buckets), [buckets]);

  const defaultCurrentRowKey = useMemo(
    () => bucketViews.find(b => b.avg_days_overdue < 0)?.rowKey ?? null,
    [bucketViews]
  );

  const selectedRowKey =
    selectionOverride === undefined ? defaultCurrentRowKey : selectionOverride;

  const activeBucket = useMemo(
    () => bucketViews.find(b => b.rowKey === selectedRowKey) ?? null,
    [bucketViews, selectedRowKey]
  );

  const handleSelectRowKey = (rowKey: string) => {
    if (rowKey === '') {
      setSelectionOverride(null);
      return;
    }
    setSelectionOverride(prev => {
      const effective = prev === undefined ? defaultCurrentRowKey : prev;
      return effective === rowKey ? null : rowKey;
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 pt-3 pb-2.5 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-0.5 h-4 rounded-full bg-red-500 flex-none" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
              Outstanding by Aging Period
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span className="text-[10px] text-slate-400 hidden md:inline">
              {view === 'chart'
                ? 'Click a bar to view client details'
                : 'Click a row to view client details'}
            </span>
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
              {(['chart', 'table'] as const).map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors capitalize ${
                    view === v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'
                  }`}
                >
                  {v === 'chart' ? 'Chart' : 'Table'}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4">
          {view === 'chart' ? (
            <AgingBucketChart
              embedded
              buckets={bucketViews}
              selectedRowKey={selectedRowKey}
              onSelectRowKey={handleSelectRowKey}
            />
          ) : (
            <BucketTable
              embedded
              buckets={bucketViews}
              selectedRowKey={selectedRowKey}
              onSelectRowKey={handleSelectRowKey}
            />
          )}
        </div>
      </div>

      {activeBucket && <BucketClientList bucket={activeBucket} />}

      {!activeBucket && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-center text-[12px] text-slate-500">
          Click a row in the table or a bar in the chart to view clients within that aging period.
        </div>
      )}
    </div>
  );
};

export default AgingOverviewTab;
